'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks an element's content-box width via ResizeObserver. Returns a
 * callback ref so late-mounted nodes (portals, conditional content) are
 * observed as soon as they attach; width is 0 until then.
 */
export function useElementWidth<T extends HTMLElement>(): [
  (node: T | null) => void,
  number
] {
  const observerRef = useRef<ResizeObserver | null>(null);
  const [width, setWidth] = useState(0);

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) {
      setWidth(0);
      return;
    }
    const update = () => setWidth(node.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    observerRef.current = new ResizeObserver(update);
    observerRef.current.observe(node);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, width];
}
